type ContentSection = { id: string; title: string; content: string[] };

export type DocumentLink = { id: string; label: string; url: string };

export type MultiTermsBaseContentType = {
  title: string;
  sections: ContentSection[];
  documents: Record<string, { title: string; links: DocumentLink[] }>;
  banner: Record<string, string>;
  important: ContentSection;
} & { loadingSpinner?: string };

export const MultiTermsBaseContent: MultiTermsBaseContentType = {
  title: `Sign agreements and submit your request`,
  sections: [
    {
      id: 'aboutBrokerage',
      title: `About opening your new brokerage account(s)`,
      content: [
        `You are opening account(s) through Vanguard Brokerage Services® (VBS), a division of Vanguard Marketing Corporation (VMC), member FINRA and SIPC, to purchase and sell stocks, bonds, ETFs, Vanguard and non-Vanguard mutual funds, and other securities.`,
        `Assets identified in this change of ownership application will be transferred into this new account.`,
      ],
    },
    {
      id: 'reviewCrsForm',
      title: `Review our Form CRS`,
      content: [
        `Please read our Form CRS presented below to help you better understand the services, fees, costs, conflicts, and conduct standards that apply to your relationship with Vanguard Brokerage Services.`,
        `<a class="link-underline c11n-link c11n-link--bold c11n-link--reinforced" href="https://personal1.vanguard.com/pdf/vmccrs.pdf" target="_blank" rel="noopener noreferrer">Form CRS</a>`,
      ],
    },
    {
      id: 'deliveryConsent',
      title: `Consent to electronic delivery`,
      content: [
        `<strong>How to access online documents:</strong>`,
        `To view and print the documents listed below, you'll need software that reads PDF files. If you can’t obtain the documents, or you don’t consent to electronic delivery, you can’t submit an application through this process.`,
        `Contact us to request a free copy of these documents.`,
        `Vanguard won’t charge you for using our website (but you could incur expenses from your service provider).`,
        `Internet service providers may occasionally experience system failures, and hyperlinks may fail to function properly.<br>`,
        `<strong>Duration of electronic consent:</strong>`,
        `This election and consent applies only to the documents required to complete this specific transaction.`,
      ],
    },
  ],
  documents: {
    forAll: {
      title: `Documents for all account types:`,
      links: [
        {
          id: 'formCrs',
          label: `Form CRS`,
          url: `https://personal1.vanguard.com/pdf/vmccrs.pdf`,
        },
        {
          id: 'brokerageAccount',
          label: `Vanguard Brokerage Account Agreement`,
          url: `https://personal1.vanguard.com/pdf/vbsaacp.pdf`,
        },
        {
          id: 'allFederalMarketFund',
          label: `Vanguard Federal Money Market Fund (PDF)`,
          url: `https://personal1.vanguard.com/pub/Pdf/sp33.pdf`,
        },
        {
          id: 'privacyPolicy',
          label: `Vanguard’s Privacy Policy (PDF)`,
          url: `https://investor.vanguard.com/investing/privacy-policy`,
        },
        {
          id: 'commissionAndFee',
          label: `Vanguard Brokerage Services commission and fee schedules`,
          url: `https://personal1.vanguard.com/pdf/v414.pdf`,
        },
        {
          id: 'recoveryProgram',
          label: `Business Contingency Planning and Disaster Recovery Programs at Vanguard`,
          url: `https://www.vanguard.com/pdf/s845.pdf`,
        },
      ],
    },
    forSettlement: {
      title: `Documents for settlement funds (as applicable):`,
      links: [
        {
          id: 'settlementFederalMarketFund',
          label: `Vanguard Federal Money Market Fund (PDF)`,
          url: `https://personal1.vanguard.com/pub/Pdf/sp33.pdf`,
        },
        {
          id: 'cashDeposit',
          label: `Vanguard Bank Sweep Products Terms Of Use (PDF) - Vanguard Cash Deposit`,
          url: `https://personal1.vanguard.com/pdf/Bank_Sweep_Terms_of_Use.pdf`,
        },
      ],
    },
  },
  banner: {
    title: `By clicking <strong>Agree and sign:</strong>`,
    readAndAccept: `I have read and accept the terms and conditions above and the <a class="link-underline c11n-link c11n-link--bold c11n-link--reinforced" href="https://personal1.vanguard.com/pdf/vbsaacp.pdf" target="_blank" rel="noopener noreferrer">Vanguard Brokerage Account Agreement</a> and, if applicable, <a class="link-underline c11n-link c11n-link--bold c11n-link--reinforced" href="https://personal.vanguard.com/pdf/s831.pdf" target="_blank" rel="noopener noreferrer">The Vanguard Traditional IRA, SEP-IRA, and Roth IRA Disclosure Statement and Custodial Account Agreement</a>`,
    authorizeTheTransfer: `I authorize the transfer of assets as indicated in this application.`,
    lawRef: `I acknowledge that each brokerage account is governed by a predispute arbitration clause in Section 9(f) on page 18 of the Vanguard Brokerage Account Agreement.`,
    buttonLabel: `Agree and sign`,
  },
  important: {
    id: 'importantInformation',
    title: `Important information:`,
    content: [
      `Vanguard Marketing Corporation is required by federal law to obtain from each person who opens an account, and in some cases, regarding each owner or trustee, certain personal information including name, street address, and taxpayer identification number, among other information-that will be used to verify identity. If you don't provide us with this information, we won't be able to open the account. If we're unable to verify your identity, Vanguard Marketing Corporation reserves the right to close your account or take other steps we deem reasonable`,
    ],
  },
};
